import _ from 'lodash';

import { hasQueryOverrides } from 'components/V5Gadget/seriesDataKey';
import aguid from 'lib/aguid';

type WithSeries = Pick<Studio.Card, 'metricIds' | 'seriesVariants'>;

// The charts whose series can ask their own question (see
// V5SeriesQueryOverrides)
const VARIANT_VIS_TYPES: Studio.VisType[] = [
  'bar',
  'combo',
  'line',
  'pictorialBar',
];

// A Table's metrics can too while each is a column of its own: metric totals
// by at most one dimension (or by date), a column per metric, not reversed
// (see canHaveOverrideGroups). The table's own metrics pick its rows.
const isMetricColumnsTable = ({
  table,
  dimensions,
}: Pick<Studio.ChartContent, 'table' | 'dimensions'>): boolean => {
  const options = table ?? {};
  return (
    (!options.rows || options.rows === 'totals') &&
    (!options.columns || options.columns === 'metrics') &&
    !options.reverseAxes &&
    (dimensions ?? []).length <= 1
  );
};

export const supportsSeriesVariants = ({
  visType,
  table,
  dimensions,
}: Pick<Studio.ChartContent, 'visType' | 'table' | 'dimensions'>): boolean =>
  !!visType &&
  (VARIANT_VIS_TYPES.includes(visType) ||
    (visType === 'table' && isMetricColumnsTable({ table, dimensions })));

export const getSeriesVariant = ({
  card,
  key,
}: {
  card: WithSeries;
  key: string;
}): Studio.SeriesVariant | undefined =>
  card.seriesVariants ? card.seriesVariants[key] : undefined;

// The metric an entry of metricIds draws: a variant's, or the entry itself
export const resolveSeriesMetricId = ({
  card,
  key,
}: {
  card: WithSeries;
  key: string;
}): string => {
  const variant = getSeriesVariant({ card, key });
  return variant ? variant.metricId : key;
};

// Whether any of its series is a variant, so draws a metric other than as the
// block asks it (or draws one more than once)
export const hasSeriesVariants = (card: WithSeries): boolean =>
  (card.metricIds ?? []).some((key) => !!getSeriesVariant({ card, key }));

// Every metric the block draws, once each
export const getResolvedMetricIds = (card: WithSeries): string[] =>
  _.uniq(
    (card.metricIds ?? []).map((key) => resolveSeriesMetricId({ card, key })),
  );

// What a variant asks differently, as saved on its chart series
export const toQueryOverrides = (
  variant: Studio.SeriesVariant | undefined,
): V5SeriesQueryOverrides | undefined => {
  if (!variant) {
    return undefined;
  }
  const { isFiltersOverridden, filters, dateOffset, dateField } = variant;
  const overrides: V5SeriesQueryOverrides = {
    ...(isFiltersOverridden ? { isFiltersOverridden } : {}),
    ...(filters && filters.length > 0 ? { filters } : {}),
    ...(dateOffset && dateOffset.count > 0 ? { dateOffset } : {}),
    ...(dateField ? { dateField } : {}),
  };
  return hasQueryOverrides(overrides) ? overrides : undefined;
};

// A series' key in the chart's data (see seriesDataKey): its chart series'
// id (see buildChartDefinition) when it asks its own question, else its
// metric. Settings keyed by series are looked up by it once drawn.
export const toSeriesDataKey = ({
  card,
  key,
}: {
  card: WithSeries & Pick<Studio.Card, 'id'>;
  key: string;
}): string =>
  toQueryOverrides(getSeriesVariant({ card, key }))
    ? `${card.id}-${key}`
    : resolveSeriesMetricId({ card, key });

// Moves a per-series setting from one key to another
const reKey = <T>(
  lookup: Record<string, T> | undefined,
  from: string,
  to: string,
): Record<string, T> | undefined => {
  if (!lookup || !(from in lookup)) {
    return lookup;
  }
  const { [from]: moved, ...rest } = lookup;
  return { ...rest, [to]: moved };
};

// Anything with series: a chart block, or a Composite's chart element
type SeriesContent = WithSeries &
  Pick<Studio.Card, 'chart' | 'chartColors' | 'table'>;

// Its options, colour, (Pictorial Bar) icon and Table column options follow a
// series to its new key
const reKeySeriesSettings = <C extends SeriesContent>({
  card,
  from,
  to,
}: {
  card: C;
  from: string;
  to: string;
}): C => ({
  ...card,
  ...(card.chart
    ? {
        chart: {
          ...card.chart,
          metrics: reKey(card.chart.metrics, from, to),
          ...(card.chart.pictorial
            ? {
                pictorial: {
                  ...card.chart.pictorial,
                  metricIcons: reKey(
                    card.chart.pictorial.metricIcons,
                    from,
                    to,
                  ),
                },
              }
            : {}),
        },
      }
    : {}),
  ...(card.chartColors
    ? {
        chartColors: {
          ...card.chartColors,
          metrics: reKey(card.chartColors.metrics, from, to),
        },
      }
    : {}),
  ...(card.table && card.table.metrics
    ? {
        table: {
          ...card.table,
          metrics: reKey(card.table.metrics, from, to),
        },
      }
    : {}),
});

/**
 * A plain metric series as a variant (keeping its place and settings), so it
 * can be given overrides. Returns the variant's key; a variant is returned as
 * it is.
 */
export const toVariant = <C extends SeriesContent>({
  card,
  key,
}: {
  card: C;
  key: string;
}): { card: C; key: string } => {
  if (getSeriesVariant({ card, key })) {
    return { card, key };
  }
  const variantKey = aguid();
  const reKeyed = reKeySeriesSettings({ card, from: key, to: variantKey });
  return {
    key: variantKey,
    card: {
      ...reKeyed,
      metricIds: (card.metricIds ?? []).map((k) =>
        k === key ? variantKey : k,
      ),
      seriesVariants: {
        ...(card.seriesVariants ?? {}),
        [variantKey]: { metricId: key },
      },
    },
  };
};

// Another series of the same metric, asking the same question, after it
export const duplicateSeries = <C extends SeriesContent>({
  card,
  key,
}: {
  card: C;
  key: string;
}): { card: C; key: string } => {
  const variantKey = aguid();
  const source = getSeriesVariant({ card, key }) ?? {
    metricId: key,
  };
  const metricIds = card.metricIds ?? [];
  const index = metricIds.indexOf(key);
  return {
    key: variantKey,
    card: {
      ...card,
      metricIds: [
        ...metricIds.slice(0, index + 1),
        variantKey,
        ...metricIds.slice(index + 1),
      ],
      seriesVariants: {
        ...(card.seriesVariants ?? {}),
        [variantKey]: _.cloneDeep(source),
      },
    },
  };
};

/**
 * The series drawing another metric, keeping its place and settings. A
 * variant keeps its key and question; a plain metric's key is the new metric,
 * its settings following it. Returns the series' key after the change. A
 * plain metric already listed can't be picked for a plain series (it would
 * be listed twice): the card is returned as it is.
 */
export const replaceSeriesMetric = <C extends SeriesContent>({
  card,
  key,
  metricId,
}: {
  card: C;
  key: string;
  metricId: string;
}): { card: C; key: string } => {
  const variant = getSeriesVariant({ card, key });
  if (variant) {
    return {
      key,
      card: {
        ...card,
        seriesVariants: {
          ...(card.seriesVariants ?? {}),
          [key]: { ...variant, metricId },
        },
      },
    };
  }
  const metricIds = card.metricIds ?? [];
  if (metricId === key || metricIds.includes(metricId)) {
    return { card, key };
  }
  return {
    key: metricId,
    card: {
      ...reKeySeriesSettings({ card, from: key, to: metricId }),
      metricIds: metricIds.map((k) => (k === key ? metricId : k)),
    },
  };
};

export const setVariantOverrides = <C extends SeriesContent>({
  card,
  key,
  overrides,
}: {
  card: C;
  key: string;
  overrides: V5SeriesQueryOverrides;
}): C => {
  const { card: withVariant, key: variantKey } = toVariant({ card, key });
  const variant = getSeriesVariant({ card: withVariant, key: variantKey });
  if (!variant) {
    return card;
  }
  return {
    ...withVariant,
    seriesVariants: {
      ...(withVariant.seriesVariants ?? {}),
      [variantKey]: { metricId: variant.metricId, ...overrides },
    },
  };
};

// Without variants left over from removed series
export const withoutStaleVariants = <C extends SeriesContent>(card: C): C => {
  if (!card.seriesVariants) {
    return card;
  }
  const keys = card.metricIds ?? [];
  const seriesVariants = _.pickBy(card.seriesVariants, (_v, key) =>
    keys.includes(key),
  );
  if (_.isEmpty(seriesVariants)) {
    const { seriesVariants: _dropped, ...rest } = card;
    return rest as C;
  }
  return { ...card, seriesVariants };
};

/**
 * Each series as its plain metric, once each, for blocks that can't draw
 * variants. A variant's settings move to its metric unless the metric has
 * its own.
 */
export const stripVariants = <C extends SeriesContent>(card: C): C => {
  if (!card.seriesVariants || _.isEmpty(card.seriesVariants)) {
    return card;
  }
  const keys = card.metricIds ?? [];
  const reKeyed = keys.reduce<C>((acc, key) => {
    const variant = getSeriesVariant({ card, key });
    if (!variant || keys.includes(variant.metricId)) {
      return acc;
    }
    return reKeySeriesSettings({ card: acc, from: key, to: variant.metricId });
  }, card);
  const { seriesVariants: _dropped, ...rest } = reKeyed;
  return {
    ...rest,
    metricIds: getResolvedMetricIds(card),
  } as C;
};
